/**
 * InvoicesPage.jsx — Master invoice management page (MUI)
 */

import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Button, Grid, IconButton, Chip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useInvoices } from '../hooks/useInvoices';
import { useMobile } from '../hooks/useMobile';
import { useXeroStatus } from '../hooks/useXero';
import InvoiceStatsBar from '../components/invoices/InvoiceStatsBar';
import InvoiceList from '../components/invoices/InvoiceList';
import InvoiceDetail from '../components/invoices/InvoiceDetail';
import StandaloneInvoiceForm from '../components/invoices/StandaloneInvoiceForm';
import FocusHide from '../components/shared/FocusHide';

export default function InvoicesPage() {
  const [desktopSelectedInvoice, setDesktopSelectedInvoice] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const { data: allInvoices = [] } = useInvoices();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile } = useMobile();
  const navigate = useNavigate();
  const { data: xeroStatus } = useXeroStatus();
  const xeroConnected = xeroStatus?.connected === true;

  // URL-driven selection for mobile
  const selectParam = searchParams.get('select');
  const mobileSelectedInvoice = selectParam ? allInvoices.find((inv) => inv.id === selectParam) : null;
  const selectedInvoice = isMobile ? mobileSelectedInvoice : desktopSelectedInvoice;

  const handleSelect = (inv) => {
    if (isMobile) {
      const sp = new URLSearchParams(searchParams);
      sp.set('select', inv.id);
      setSearchParams(sp, { replace: false });
    } else {
      setDesktopSelectedInvoice(inv);
    }
    setShowNewForm(false);
  };

  const handleBack = () => {
    if (window.history.state?.idx > 0) {
      navigate(-1);
    } else {
      const sp = new URLSearchParams(searchParams);
      sp.delete('select');
      setSearchParams(sp, { replace: true });
    }
    setShowNewForm(false);
  };

  const handleClose = () => {
    if (isMobile) {
      handleBack();
    } else {
      setDesktopSelectedInvoice(null);
    }
  };

  // Clear form state when browser back removes selectParam
  useEffect(() => {
    if (isMobile && !selectParam) {
      setShowNewForm(false);
    }
  }, [isMobile, selectParam]);

  // Desktop: select from ?select= once the list has loaded, then clear the param
  // (JobsPage pattern, so a cold-loaded link still lands). Mobile keeps the param:
  // it drives the detail view and the back button.
  useEffect(() => {
    if (isMobile || !selectParam || allInvoices.length === 0) return;
    const found = allInvoices.find((inv) => inv.id === selectParam);
    if (found) {
      setDesktopSelectedInvoice(found);
      setShowNewForm(false);
    }
    const sp = new URLSearchParams(searchParams);
    sp.delete('select');
    setSearchParams(sp, { replace: true });
  }, [selectParam, allInvoices, isMobile, searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get('action') === 'newInvoice') {
      setShowNewForm(true);
      if (isMobile) {
        const sp = new URLSearchParams(searchParams);
        sp.delete('action');
        sp.set('select', '__new');
        setSearchParams(sp, { replace: true });
      } else {
        setDesktopSelectedInvoice(null);
        const sp = new URLSearchParams(searchParams);
        sp.delete('action');
        setSearchParams(sp, { replace: true });
      }
    }
  }, [searchParams, setSearchParams]);

  // Mobile: list→detail toggle
  const showDetail = isMobile && (selectParam || showNewForm);

  if (isMobile) {
    return (
      <Box>
        {showDetail ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <IconButton size="small" onClick={handleBack}><ArrowBackIcon /></IconButton>
              <Typography variant="h6" sx={{ fontSize: '1rem' }}>{showNewForm ? 'New Invoice' : 'Invoice Detail'}</Typography>
            </Box>
            {showNewForm ? (
              <StandaloneInvoiceForm onSave={() => handleBack()} onCancel={() => handleBack()} />
            ) : selectedInvoice ? (
              <InvoiceDetail invoice={selectedInvoice} onClose={handleClose} />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>Invoice not found</Typography>
            )}
          </Box>
        ) : (
          <Box>
            <Typography variant="h5" sx={{ fontFamily: '"Bebas Neue", sans-serif', letterSpacing: 1, mb: 2 }}>Invoices</Typography>
            {xeroConnected && <FocusHide k="invoices.xeroChip"><Chip label="Xero Connected" size="small" color="success" variant="outlined" sx={{ mb: 2 }} onClick={() => navigate('/settings?tab=integrations')} /></FocusHide>}
            <InvoiceStatsBar />
            <Box sx={{ mt: 1 }}>
              <InvoiceList selectedInvoiceId={null} onSelect={handleSelect} onNewInvoice={() => { setShowNewForm(true); const sp = new URLSearchParams(searchParams); sp.set('select', '__new'); setSearchParams(sp, { replace: false }); }} />
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box data-track-area="invoices">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontFamily: '"Bebas Neue", sans-serif', letterSpacing: 1 }}>Invoices</Typography>
        {xeroConnected && <FocusHide k="invoices.xeroChip"><Chip label="Xero Connected" size="small" color="success" variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => navigate('/settings?tab=integrations')} /></FocusHide>}
      </Box>
      <InvoiceStatsBar />
      <Grid container spacing={2} sx={{ height: 'calc(100dvh - 240px)', mt: 1 }}>
        <Grid size={{ xs: 12, lg: 6 }} sx={{ minHeight: 0 }}>
          <InvoiceList selectedInvoiceId={desktopSelectedInvoice?.id} onSelect={handleSelect} onNewInvoice={() => { setShowNewForm(true); setDesktopSelectedInvoice(null); }} />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }} sx={{ minHeight: 0 }}>
          {showNewForm ? (
            <StandaloneInvoiceForm onSave={() => setShowNewForm(false)} onCancel={() => setShowNewForm(false)} />
          ) : desktopSelectedInvoice ? (
            <InvoiceDetail invoice={desktopSelectedInvoice} onClose={() => setDesktopSelectedInvoice(null)} />
          ) : (
            <Paper sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Select an invoice to view details</Typography>
                <FocusHide k="invoices.emptyCreate">
                  <Button variant="contained" onClick={() => { setShowNewForm(true); setDesktopSelectedInvoice(null); }}>+ Create New Invoice</Button>
                </FocusHide>
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
